import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogIn, KeyRound, HelpCircle } from "lucide-react";

const HERO_URL = "https://images.pexels.com/photos/5549509/pexels-photo-5549509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LOGO_URL = "/nphs-logo.png";

export default function Login() {
  const { login, setupPassword, user } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "setup"
  const [pendingSetups, setPendingSetups] = useState([]); // [{email, role, needs_setup}]
  const [needsSetupEmail, setNeedsSetupEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const nav = useNavigate();

  const roleLabel = (r) => (r === "builder" ? "Tech Support" : r === "coach" ? "Coach Derrick Canada" : "Account");

  const applyPending = (pendings, preferredEmail) => {
    if (pendings.length === 0) {
      setMode("login");
      setPendingSetups([]);
      return;
    }
    setMode("setup");
    setPendingSetups(pendings);
    const chosen = pendings.find((p) => p.email === preferredEmail) || pendings[0];
    setNeedsSetupEmail(chosen.email);
    setSelectedRole(chosen.role);
    setEmail(chosen.email);
    setPassword("");
    setConfirm("");
  };

  const refreshSetupStatus = (preferredEmail = null) => api.get("/auth/setup-status").then(({ data }) => {
    const pendings = (data.coaches || []).filter((c) => c.needs_setup);
    applyPending(pendings, preferredEmail);
  }).catch(() => {});

  useEffect(() => {
    if (user) nav("/dashboard");
  }, [user, nav]);

  useEffect(() => {
    refreshSetupStatus();
  }, []);

  const switchPending = () => {
    if (pendingSetups.length < 2) return;
    const other = pendingSetups.find((p) => p.email !== needsSetupEmail);
    if (other) applyPending(pendingSetups, other.email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let signedInUser;
      if (mode === "setup") {
        if (password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return;
        }
        if (password !== confirm) {
          toast.error("Passwords do not match");
          return;
        }
        signedInUser = await setupPassword(email, password);
        toast.success(`Password set! Welcome, ${signedInUser?.name || roleLabel(signedInUser?.role)}.`);
      } else {
        signedInUser = await login(email, password);
        toast.success(`Welcome back, ${signedInUser?.name || roleLabel(signedInUser?.role)}.`);
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex relative flex-1 items-center justify-center overflow-hidden">
        <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-black/60 to-transparent" />
        <div className="relative px-16 max-w-xl">
          <img src={LOGO_URL} alt="NPHS" className="w-20 h-20 rounded-full bg-black/40 ring-2 ring-burgundy mb-8" />
          <p className="eyebrow">North Plainfield High School</p>
          <h1 className="heading-1 mt-3">
            NPHS <span className="text-burgundy">Canucks</span>
            <br />Stats Command
          </h1>
          <p className="text-ink-300 mt-6 max-w-md leading-relaxed">
            The official varsity basketball stats platform. Log every game, every shot, every win — built for Coach Derrick Canada and the boys in burgundy.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-ink-950">
        <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="NPHS" className="w-12 h-12 rounded-full ring-2 ring-burgundy bg-black" />
            <div>
              <p className="font-display text-2xl font-black uppercase">NPHS Canucks</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-ink-300">Stats Platform</p>
            </div>
          </div>

          <p className="eyebrow">{mode === "setup" ? "First-Time Setup" : "Sign In"}</p>
          <h2 className="heading-2 mt-2 mb-1">
            {mode === "setup" ? "Set Your Password" : "Welcome Back"}
          </h2>

          {mode === "setup" ? (
            <div className="mt-3 mb-6 p-3 rounded-md bg-burgundy/15 border border-burgundy/40" data-testid="setup-account-card">
              <p className="eyebrow text-[9px]">Setting up account</p>
              <p className="font-display text-lg font-bold uppercase leading-tight text-white mt-1">
                {roleLabel(selectedRole)}
              </p>
              <p className="text-ink-300 text-xs font-mono mt-1 break-all">{needsSetupEmail}</p>
              {pendingSetups.length > 1 && (
                <button
                  type="button"
                  onClick={switchPending}
                  className="mt-2 text-[11px] uppercase tracking-wider text-burgundy hover:text-burgundy-hover font-bold"
                  data-testid="switch-pending-account-btn"
                >
                  Not you? Switch to {roleLabel(pendingSetups.find((p) => p.email !== needsSetupEmail)?.role)} →
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-300 text-sm mb-8">
              Visitors and players don&apos;t need to log in. This is for authorized accounts only.
            </p>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled={mode === "setup"}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="coach@nplainfield.org"
                className="mt-2 bg-ink-900 border-ink-700 h-11 text-white"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 bg-ink-900 border-ink-700 h-11 text-white"
                data-testid="login-password-input"
                required
              />
            </div>
            {mode === "setup" && (
              <div>
                <Label htmlFor="confirm" className="eyebrow">Confirm Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 bg-ink-900 border-ink-700 h-11 text-white"
                  data-testid="login-confirm-input"
                  required
                />
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full mt-6 h-12 bg-burgundy hover:bg-burgundy-hover uppercase tracking-wider font-bold"
            data-testid="login-submit-button"
          >
            {mode === "setup" ? <><KeyRound className="w-4 h-4 mr-2" /> Activate Account</> : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
          </Button>

          {mode === "login" && (
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="block w-full mt-3 text-center text-xs uppercase tracking-wider text-ink-300 hover:text-burgundy transition"
              data-testid="forgot-password-link"
            >
              <HelpCircle className="w-3 h-3 inline mr-1 -mt-0.5" /> Forgot password?
            </button>
          )}

          <p className="text-xs text-ink-400 text-center mt-6">
            Not a coach? <a href="/schedule" className="text-burgundy hover:text-burgundy-hover underline">View the public schedule →</a>
          </p>
        </form>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        onReset={async () => {
          setForgotOpen(false);
          await refreshSetupStatus();
        }}
      />
    </div>
  );
}

function ForgotPasswordDialog({ open, onClose, onReset }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, reset_code: code });
      toast.success("Password reset. You can now set a new one.");
      setEmail("");
      setCode("");
      onReset();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="heading-3 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-burgundy" /> Forgot Password
          </DialogTitle>
          <DialogDescription className="text-ink-300">
            Enter your coach email and the admin reset code. Once verified, you&apos;ll be bounced back to the &quot;Set Your Password&quot; screen to pick a new one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
          <div>
            <Label className="eyebrow">Coach Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="coach@nplainfield.org"
              className="bg-ink-800 border-ink-700 mt-2"
              data-testid="forgot-email-input"
              required
            />
          </div>
          <div>
            <Label className="eyebrow">Admin Reset Code</Label>
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Set in backend .env"
              className="bg-ink-800 border-ink-700 mt-2 font-mono text-sm"
              data-testid="forgot-code-input"
              required
            />
            <p className="text-[10px] text-ink-400 mt-1">
              The code lives in <code className="text-ink-300">backend/.env</code> as <code className="text-ink-300">ADMIN_RESET_CODE</code>.
            </p>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="forgot-submit-btn">
            {busy ? "Resetting…" : "Reset Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
